export interface BotTheme {
  id: string;
  name: string;
  primaryColor: string;
  backgroundColor: string;
  headerBg: string;
  headerText: string;
  userBubble: string;
  userBubbleText: string;
  botBubble: string;
  botBubbleText: string;
  fontFamily: string;
  borderRadius: string;
}

// All themes are white-backgrounded (light) — dark mode has been
// removed. Themes only differ by their accent / brand color so the
// chat surface always matches the rest of the light Relatus-style
// staff UI. Bot bubbles render as a soft slate card and user bubbles
// pick up the theme's primary color.
const LIGHT_BG = "#ffffff";
const LIGHT_HEADER_BG = "#ffffff";
const LIGHT_HEADER_TEXT = "#0f172a";
const LIGHT_BOT_BUBBLE = "#f1f5f9";
const LIGHT_BOT_TEXT = "#0f172a";
const WHITE = "#ffffff";

function lightTheme(
  id: string,
  name: string,
  primary: string,
  opts: { fontFamily?: string; borderRadius?: string } = {},
): BotTheme {
  return {
    id,
    name,
    primaryColor: primary,
    backgroundColor: LIGHT_BG,
    headerBg: LIGHT_HEADER_BG,
    headerText: LIGHT_HEADER_TEXT,
    userBubble: primary,
    userBubbleText: WHITE,
    botBubble: LIGHT_BOT_BUBBLE,
    botBubbleText: LIGHT_BOT_TEXT,
    fontFamily: opts.fontFamily ?? "Inter, sans-serif",
    borderRadius: opts.borderRadius ?? "16px",
  };
}

export const BOT_THEMES: BotTheme[] = [
  lightTheme("professional-blue", "Professional Blue", "#0c3547"),
  // "modern-dark" id retained for backwards compatibility with bots
  // that were saved with this id; rendered as a light slate theme now.
  lightTheme("modern-dark", "Modern Slate", "#334155"),
  lightTheme("warm-orange", "Warm Orange", "#ea580c", { fontFamily: "Poppins, sans-serif", borderRadius: "20px" }),
  lightTheme("nature-green", "Nature Green", "#16a34a"),
  lightTheme("corporate-navy", "Corporate Navy", "#1e3a5f", { fontFamily: "Source Sans Pro, sans-serif", borderRadius: "12px" }),
  lightTheme("minimal-light", "Minimal Light", "#18181b", { borderRadius: "10px" }),
  lightTheme("sunset-gradient", "Sunset Pink", "#be185d", { fontFamily: "Poppins, sans-serif", borderRadius: "18px" }),
  lightTheme("tech-purple", "Tech Purple", "#7c3aed", { fontFamily: "Space Grotesk, sans-serif" }),
];

export function getThemeById(id: string): BotTheme {
  return BOT_THEMES.find(t => t.id === id) || BOT_THEMES[0];
}

// Per-bot color overrides — any field left null/empty falls through
// to the chosen theme's value, so a bot can tweak only the colors it
// cares about without losing the rest of the theme.
export interface ThemeOverrides {
  theme?: string | null;
  primaryColor?: string | null;
  backgroundColor?: string | null;
  botBubbleColor?: string | null;
  botTextColor?: string | null;
  userBubbleColor?: string | null;
  userTextColor?: string | null;
}

// ---------------------------------------------------------------------------
// WCAG 1.4.3 runtime contrast guard for admin-configured bubble colors.
// Admins can pick arbitrary hex values; text rendered on those colors must
// still compute to >= 4.5:1. We preserve the chosen hue/saturation and only
// nudge lightness (same approach as ensureContrast in platform-themes); if a
// color string is unparseable we leave it untouched.
// ---------------------------------------------------------------------------

function hexChannel(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) {
    const s = /^#?([0-9a-f]{3})$/i.exec(hex.trim());
    if (!s) return null;
    const [r, g, b] = s[1].split("");
    return [parseInt(r + r, 16), parseInt(g + g, 16), parseInt(b + b, 16)];
  }
  const v = m[1];
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
}

function luminance(hex: string): number | null {
  const rgb = hexChannel(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatioHex(a: string, b: string): number | null {
  const la = luminance(a);
  const lb = luminance(b);
  if (la === null || lb === null) return null;
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [h * 360, s * 100, l * 100];
}

function hslToHexLocal(h: number, s: number, l: number): string {
  s /= 100; l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const to = (x: number) => Math.round(x * 255).toString(16).padStart(2, "0");
  return `#${to(f(0))}${to(f(8))}${to(f(4))}`;
}

/**
 * Return a foreground color with >= `target` contrast against `bg`,
 * preserving the requested foreground's hue/saturation where possible.
 * Falls back to near-black/white (always >= ~4.5:1 for any background).
 */
export function ensureReadableText(fg: string, bg: string, target = 4.5): string {
  const current = contrastRatioHex(fg, bg);
  if (current === null || current >= target) return fg;
  const rgb = hexChannel(fg);
  const bgLum = luminance(bg);
  if (!rgb || bgLum === null) return fg;
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  // Nudge lightness away from the background until the target is met.
  const darkenFirst = bgLum > 0.5;
  for (let step = 1; step <= 20; step++) {
    const delta = step * 5;
    const candidates = darkenFirst
      ? [Math.max(0, l - delta), Math.min(100, l + delta)]
      : [Math.min(100, l + delta), Math.max(0, l - delta)];
    for (const cl of candidates) {
      const candidate = hslToHexLocal(h, s, cl);
      const ratio = contrastRatioHex(candidate, bg);
      if (ratio !== null && ratio >= target) return candidate;
    }
  }
  // Hard fallback: whichever of black/white contrasts more. Pure black is
  // required here — for mid-gray backgrounds (~#787878) neither white nor a
  // near-black like #0f172a reaches 4.5:1, but pure black does.
  const dark = "#000000", light = "#ffffff";
  return (contrastRatioHex(dark, bg) ?? 0) >= (contrastRatioHex(light, bg) ?? 0) ? dark : light;
}

/** Best plain readable text color (white or black) for an arbitrary background. */
export function readableTextOn(bg: string): string {
  const dark = "#000000", light = "#ffffff";
  return (contrastRatioHex(light, bg) ?? 0) >= (contrastRatioHex(dark, bg) ?? 0) ? light : dark;
}

export interface ThemeContrastAdjustments {
  botText: boolean;
  userText: boolean;
}

/**
 * Resolve a bot theme AND clamp the text-on-bubble pairs to >= 4.5:1
 * (WCAG 1.4.3). Backgrounds keep the admin's exact choice; only the
 * foreground is adjusted (hue-preserving) when the pair would fail.
 */
export function resolveThemeWithContrast(
  overrides: ThemeOverrides | null | undefined,
): { theme: BotTheme; adjusted: ThemeContrastAdjustments } {
  const raw = resolveTheme(overrides);
  const botBubbleText = ensureReadableText(raw.botBubbleText, raw.botBubble);
  const userBubbleText = ensureReadableText(raw.userBubbleText, raw.userBubble);
  return {
    theme: { ...raw, botBubbleText, userBubbleText },
    adjusted: {
      botText: botBubbleText !== raw.botBubbleText,
      userText: userBubbleText !== raw.userBubbleText,
    },
  };
}

export function resolveTheme(overrides: ThemeOverrides | null | undefined): BotTheme {
  const base = getThemeById(overrides?.theme || "professional-blue");
  return {
    ...base,
    primaryColor: overrides?.primaryColor || base.primaryColor,
    backgroundColor: overrides?.backgroundColor || base.backgroundColor,
    botBubble: overrides?.botBubbleColor || base.botBubble,
    botBubbleText: overrides?.botTextColor || base.botBubbleText,
    userBubble: overrides?.userBubbleColor || base.userBubble,
    userBubbleText: overrides?.userTextColor || base.userBubbleText,
  };
}
